import {
  AuthErrorCodes,
  FactorId,
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  UserCredential,
  getMultiFactorResolver,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';

export const MFA_RECAPTCHA_CONTAINER_ID = 'login-mfa-recaptcha-container';
export const MFA_RECAPTCHA_WRAPPER_ID = 'login-mfa-recaptcha-wrapper';

export type MfaFactorType = 'phone' | 'totp';

export interface MfaChallengeSession {
  resolver: MultiFactorResolver;
  factorType: MfaFactorType;
  hint: MultiFactorInfo;
  /** Present after SMS is sent for phone MFA. */
  verificationId: string | null;
  phoneNumber: string | null;
}

declare global {
  interface Window {
    loginMfaRecaptchaVerifier?: RecaptchaVerifier;
  }
}

export function isMfaRequiredError(error: unknown): error is MultiFactorError {
  return (error as { code?: string })?.code === AuthErrorCodes.MFA_REQUIRED;
}

function selectHint(resolver: MultiFactorResolver): MultiFactorInfo {
  const phoneHint = resolver.hints.find((hint) => hint.factorId === FactorId.PHONE);
  if (phoneHint) {
    return phoneHint;
  }

  const totpHint = resolver.hints.find((hint) => hint.factorId === FactorId.TOTP);
  if (totpHint) {
    return totpHint;
  }

  throw new Error('No supported MFA method is enrolled on this account. Please use phone or authenticator MFA.');
}

function resetRecaptchaContainer() {
  const wrapper = document.getElementById(MFA_RECAPTCHA_WRAPPER_ID);
  if (wrapper) {
    wrapper.innerHTML = `<div id="${MFA_RECAPTCHA_CONTAINER_ID}"></div>`;
    return;
  }

  let container = document.getElementById(MFA_RECAPTCHA_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = MFA_RECAPTCHA_CONTAINER_ID;
    document.body.appendChild(container);
  } else {
    container.innerHTML = '';
  }
}

function clearRecaptchaVerifier() {
  try {
    window.loginMfaRecaptchaVerifier?.clear();
  } catch {
    // verifier may already be disposed
  }
  window.loginMfaRecaptchaVerifier = undefined;
  resetRecaptchaContainer();
}

async function ensureRecaptchaVerifier(): Promise<RecaptchaVerifier> {
  if (typeof window === 'undefined') {
    throw new Error('reCAPTCHA is only available in the browser');
  }

  if (!document.getElementById(MFA_RECAPTCHA_CONTAINER_ID)) {
    resetRecaptchaContainer();
  }

  // Always recreate: Firebase requires a fresh verifier after each verifyPhoneNumber attempt.
  clearRecaptchaVerifier();
  window.loginMfaRecaptchaVerifier = new RecaptchaVerifier(
    MFA_RECAPTCHA_CONTAINER_ID,
    {
      size: 'invisible',
      callback: () => {
        // Invoked after reCAPTCHA is solved; verifyPhoneNumber continues.
      },
      'expired-callback': () => {
        clearRecaptchaVerifier();
      },
    },
    auth,
  );
  await window.loginMfaRecaptchaVerifier.render();

  return window.loginMfaRecaptchaVerifier;
}

async function sendPhoneMfaCode(resolver: MultiFactorResolver, hint: MultiFactorInfo): Promise<string> {
  const appVerifier = await ensureRecaptchaVerifier();
  const phoneAuthProvider = new PhoneAuthProvider(auth);

  try {
    return await phoneAuthProvider.verifyPhoneNumber(
      {
        multiFactorHint: hint,
        session: resolver.session,
      },
      appVerifier,
    );
  } catch (error) {
    clearRecaptchaVerifier();
    throw error;
  }
}

/**
 * After email/password returns MFA_REQUIRED, start the second-factor challenge.
 * Phone MFA sends an SMS immediately; TOTP waits for the authenticator code.
 */
export async function beginMfaChallenge(error: MultiFactorError): Promise<MfaChallengeSession> {
  const resolver = getMultiFactorResolver(auth, error);
  const hint = selectHint(resolver);

  if (hint.factorId === FactorId.PHONE) {
    const verificationId = await sendPhoneMfaCode(resolver, hint);
    return {
      resolver,
      factorType: 'phone',
      hint,
      verificationId,
      phoneNumber: (hint as PhoneMultiFactorInfo).phoneNumber || null,
    };
  }

  return {
    resolver,
    factorType: 'totp',
    hint,
    verificationId: null,
    phoneNumber: null,
  };
}

/** Resend SMS for an in-progress phone MFA challenge. */
export async function resendPhoneMfaCode(session: MfaChallengeSession): Promise<string> {
  if (session.factorType !== 'phone') {
    throw new Error('Resend is only available for phone MFA.');
  }
  return sendPhoneMfaCode(session.resolver, session.hint);
}

/** Complete MFA with the SMS or authenticator code and finish Firebase sign-in. */
export async function completeMfaChallenge(session: MfaChallengeSession, code: string): Promise<UserCredential> {
  const trimmed = code.trim();

  if (session.factorType === 'phone') {
    if (!session.verificationId) {
      throw new Error('Phone verification is not ready. Please request a new code.');
    }
    const credential = PhoneAuthProvider.credential(session.verificationId, trimmed);
    const assertion = PhoneMultiFactorGenerator.assertion(credential);
    const userCredential = await session.resolver.resolveSignIn(assertion);
    clearRecaptchaVerifier();
    return userCredential;
  }

  const assertion = TotpMultiFactorGenerator.assertionForSignIn(session.hint.uid, trimmed);
  return session.resolver.resolveSignIn(assertion);
}

export function clearMfaRecaptcha(): void {
  if (typeof window === 'undefined') {
    return;
  }
  clearRecaptchaVerifier();
}
